import React, {useCallback, useEffect, useState} from "react";
import {apiV2} from "../../api/axiosConfig";
import {isAxiosError} from "axios";
import {Link, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {FacilityForm, FacilityFormData} from "./facility-form";
import {useDepartment} from "@/api/platform/useDepartments";

type Facility = {
    id: string;
    name: string;
    clientID: string;
    departmentID: string | null;
    address: {
        countryISO: string;
        countryName: string;
        region: string;
        county: string;
        town: string;
        postalCode: string;
        addressLines: string[];
        formatted: string;
    };
    position: {
        type: string;
        coordinates: [number, number]; // [longitude, latitude]
    };
    guardianIDs: string[];
};

export const EditFacility: React.FC = () => {
    const navigate = useNavigate();
    const {id} = useParams<{id: string}>();
    const [searchParams] = useSearchParams();
    const prevUrl = searchParams.get("prev") || "/facilities";

    const [facility, setFacility] = useState<Facility | null>(null);
    const [loading, setLoading] = useState(true);
    const {data: initialDepartment, isLoading: departmentLoading} = useDepartment(facility?.departmentID ?? "");
    const [saving, setSaving] = useState<boolean>(false);
    const [saved, setSaved] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [savedTimeout, setSavedTimeout] = useState<NodeJS.Timeout>();

    // Fetch facility data on mount
    useEffect(() => {
        if (!id) {
            setErrorMessage("No facility ID provided");
            setLoading(false);
            return;
        }

        const fetchFacility = async () => {
            try {
                const response = await apiV2.get<Facility>(`facilities/${id}`);
                setFacility(response.data);
            } catch (e) {
                if (isAxiosError(e) && e?.response?.data?.error) {
                    setErrorMessage(e.response.data.error);
                } else {
                    setErrorMessage("Failed to load facility");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchFacility();
    }, [id]);

    const handleSave = useCallback(async (formData: FacilityFormData) => {
        if (!formData.name) {
            setErrorMessage("Facility name is required");
            return;
        }

        if (!id) {
            setErrorMessage("No facility ID provided");
            return;
        }

        setSaved(false);
        setErrorMessage("");
        setSaving(true);

        try {
            // Build address lines array (filter out empty ones)
            const addressLines = [
                formData.addressLine1,
                formData.addressLine2,
                formData.addressLine3
            ].filter(line => line.trim() !== "");

            const payload = {
                clientID: formData.clientID,
                departmentID: formData.departmentID,
                name: formData.name,
                address: {
                    countryISO: formData.countryISO || "",
                    region: formData.region,
                    county: formData.county,
                    town: formData.town,
                    postalCode: formData.postalCode,
                    addressLines,
                },
                latitude: formData.latitude,
                longitude: formData.longitude,
                guardianIDs: formData.guardianIDs
            };

            await apiV2.patch(`facilities/${id}`, payload);

            setSaved(true);

            // Navigate back to facility list after successful update
            setTimeout(() => {
                navigate(prevUrl);
            }, 1500);

        } catch (e) {
            if (isAxiosError(e) && e?.response?.data?.error) {
                setErrorMessage(e.response.data.error);
            } else {
                setErrorMessage("An error occurred while updating the facility");
            }
        }
        setSaving(false);
    }, [id, navigate]);

    useEffect(() => {
        if (savedTimeout) {
            clearTimeout(savedTimeout);
        }

        const timeout = setTimeout(() => {
            setSaved(false);
        }, 5000);

        setSavedTimeout(timeout);
    }, [saved]);

    if (loading || departmentLoading) {
        return (
            <div className="flex flex-col gap-8">
                <div className="bg-white shadow-md p-6 w-full max-w-2xl">
                    <p>Loading facility...</p>
                </div>
            </div>
        );
    }

    if (!facility) {
        return (
            <div className="flex flex-col gap-8">
                <div className="bg-white shadow-md p-6 w-full max-w-2xl">
                    <p className="text-red">Failed to load facility</p>
                    {errorMessage && <p className="mt-2">{errorMessage}</p>}
                </div>
            </div>
        );
    }

    // Parse initial data from facility
    const initialData: Partial<FacilityFormData> = {
        clientID: facility.clientID,
        departmentID: facility?.departmentID,
        name: facility.name,
        countryISO: facility.address.countryISO,
        region: facility.address.region,
        county: facility.address.county,
        town: facility.address.town,
        postalCode: facility.address.postalCode,
        addressLine1: facility.address.addressLines[0] || "",
        addressLine2: facility.address.addressLines[1] || "",
        addressLine3: facility.address.addressLines[2] || "",
        // GeoJSON coordinates are [longitude, latitude]
        longitude: facility.position.coordinates[0],
        latitude: facility.position.coordinates[1],
        guardianIDs: facility.guardianIDs || [],
    };

    return (
        <div className="flex flex-col gap-8">
            <nav className="flex items-center gap-2 text-sm text-light-grey">
                <Link to="/sites" className="hover:text-grey">Sites</Link>
                <span>&gt;</span>
                <Link to={prevUrl} className="hover:text-grey">Facilities</Link>
                <span>&gt;</span>
                <span className="text-grey">Edit Facility</span>
            </nav>
            <div className="bg-white shadow-md p-6 w-full max-w-2xl flex flex-col gap-6">
                <h1 className="text-2xl font-semibold">Edit Facility</h1>

                <FacilityForm
                    mode="edit"
                    initialData={initialData}
                    initialDepartment={initialDepartment}
                    onSave={handleSave}
                    saving={saving}
                    saved={saved}
                    errorMessage={errorMessage}
                />
            </div>
        </div>
    );
};
